import { getRefreshToken, getTokenExpiresAt, storeTokenExpiresAt } from './tokenStorage';
import { refreshAccessToken } from './tokenRefresh';

const REFRESH_LIFETIME_FRACTION = 0.75;
const RETRY_DELAY_MS = 5_000;
const MIN_SCHEDULE_MS = 5_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearScheduledRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

async function executeProactiveRefresh(): Promise<void> {
  refreshTimer = null;

  const success = await refreshAccessToken();
  if (success) {
    return;
  }

  // Retry once after a short delay before falling back to reactive 401 handling
  await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
  await refreshAccessToken();
}

/**
 * Schedule a proactive token refresh at ~75% of the token's lifetime.
 * Call after every successful token acquisition (login or refresh).
 */
export function scheduleProactiveRefresh(expiresIn?: number): void {
  clearScheduledRefresh();

  if (!getRefreshToken()) {
    return;
  }

  let delayMs: number;

  if (expiresIn != null && expiresIn > 0) {
    storeTokenExpiresAt(expiresIn);
    delayMs = expiresIn * REFRESH_LIFETIME_FRACTION * 1000;
  } else {
    // Derive delay from stored expiry (e.g., on app startup / page reload)
    const expiresAt = getTokenExpiresAt();
    if (!expiresAt) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const remainingSec = expiresAt - nowSec;

    if (remainingSec <= 0) {
      // Token already expired — refresh immediately
      delayMs = 0;
    } else {
      delayMs = remainingSec * REFRESH_LIFETIME_FRACTION * 1000;
    }
  }

  if (delayMs < MIN_SCHEDULE_MS) {
    delayMs = MIN_SCHEDULE_MS;
  }

  refreshTimer = setTimeout(() => {
    executeProactiveRefresh().then(() => {
      // After a successful refresh, re-schedule based on stored expiry
      scheduleProactiveRefresh();
    });
  }, delayMs);
}

/**
 * Cancel any pending proactive refresh (e.g., on logout).
 */
export function cancelProactiveRefresh(): void {
  clearScheduledRefresh();
}

/**
 * Handle page visibility changes: when the tab becomes visible again,
 * check if the token is near expiry and refresh if needed.
 */
function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return;
  if (!getRefreshToken()) return;

  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) return;

  const nowSec = Math.floor(Date.now() / 1000);
  const remainingSec = expiresAt - nowSec;

  // If less than 25% of lifetime remains, refresh now
  // We don't know the original lifetime, so use an absolute threshold of 5 minutes
  if (remainingSec <= 300) {
    executeProactiveRefresh().then(() => {
      scheduleProactiveRefresh();
    });
  } else {
    // Re-schedule based on remaining time
    scheduleProactiveRefresh();
  }
}

let visibilityListenerInstalled = false;

/**
 * Install the visibility change listener (call once at app startup).
 */
export function initProactiveRefresh(): void {
  if (typeof document === 'undefined') return;

  if (!visibilityListenerInstalled) {
    visibilityListenerInstalled = true;
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // Schedule based on stored expiry if tokens already exist
  scheduleProactiveRefresh();
}
